import { useState } from 'react';
import { motion } from 'framer-motion';
import { Phone, Mail, MapPin, Send, Instagram } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

export default function ContactPage() {
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError('');
    try {
      await axios.post(`${BACKEND_URL}/api/contact`, form);
      setSubmitted(true);
      setForm({ name: '', email: '', phone: '', message: '' });
    } catch (err) {
      setError('Mesajınız gönderilemedi. Lütfen tekrar deneyin.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <main data-testid="contact-page" className="min-h-screen bg-brand-cream">
      {/* Hero */}
      <section className="relative pt-32 pb-16 md:pt-40 md:pb-24 overflow-hidden">
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-brand-pink/20 rounded-full blur-[100px] -z-10" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-brand-blue/20 rounded-full blur-[100px] -z-10" />

        <div className="container mx-auto px-4 md:px-6 max-w-7xl relative z-10">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, ease: "easeOut" }}
          >
            <span className="inline-block py-1 px-4 rounded-full bg-brand-dark/5 text-[10px] font-extrabold uppercase tracking-[0.3em] text-brand-dark/40 mb-6">
              İletişim
            </span>
            <h1 className="text-5xl md:text-7xl font-black text-brand-dark tracking-tighter leading-[1.1] mb-6">
              Bize Ulaşın
            </h1>
            <p className="text-xl text-brand-dark/50 font-medium max-w-2xl leading-relaxed">
              Sorularınız, projeleriniz veya toplu üretim talepleriniz için doğrudan uzman ekibimizle iletişime geçin.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Content */}
      <section className="pb-24">
        <div className="container mx-auto px-4 md:px-6 max-w-7xl">
          <div className="grid md:grid-cols-12 gap-12 md:gap-20">
            
            {/* Contact Info (Sol Taraf) */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6 }}
              className="md:col-span-5 space-y-10"
            >
              <div className="space-y-8">
                {[
                  { icon: Phone, label: 'Telefon', value: '0554 234 54 54', href: 'tel:+905542345454' },
                  { icon: Mail, label: 'E-posta', value: 'ayzplastik@gmail.com', href: 'mailto:ayzplastik@gmail.com' },
                  { icon: MapPin, label: 'Merkez', value: 'Konya, Türkiye', href: null },
                ].map((item) => (
                  <div key={item.label} className="flex items-center gap-6 group">
                    <div className="w-14 h-14 shrink-0 rounded-2xl bg-white border border-slate-100 flex items-center justify-center group-hover:bg-brand-dark group-hover:border-brand-dark transition-all duration-300 shadow-sm">
                      <item.icon size={20} className="text-brand-dark group-hover:text-white transition-colors duration-300" />
                    </div>
                    <div>
                      <p className="text-[10px] font-bold text-brand-dark/40 uppercase tracking-[0.2em] mb-1">{item.label}</p>
                      {item.href ? (
                        <a href={item.href} className="text-brand-dark font-bold text-lg hover:text-brand-blue transition-colors">
                          {item.value}
                        </a>
                      ) : (
                        <p className="text-brand-dark font-bold text-lg">{item.value}</p>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Social */}
              <div className="pt-8 border-t border-brand-dark/5">
                <p className="text-[10px] font-bold text-brand-dark/40 uppercase tracking-[0.2em] mb-4">Sosyal Medya</p>
                <div className="flex gap-3">
                  <a
                    href="https://www.instagram.com/ayzplastik?igsh=MWJ6c2djYTdoeTVhag%3D%3D"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-white border border-slate-100 flex items-center justify-center text-brand-dark hover:bg-brand-pink hover:text-white hover:border-brand-pink transition-all duration-300 shadow-sm"
                  >
                    <Instagram size={20} />
                  </a>
                  <a
                    href="https://wa.me/905542345454"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-12 h-12 rounded-full bg-green-500 flex items-center justify-center text-white hover:bg-green-600 transition-all duration-300 shadow-sm"
                  >
                    <Phone size={20} />
                  </a>
                </div>
              </div>

              {/* WhatsApp CTA */}
              <div className="p-6 rounded-[2rem] bg-green-50 border border-green-100">
                <p className="text-green-800 font-bold text-sm mb-2">
                  Hızlı yanıt için WhatsApp
                </p>
                <p className="text-green-700/70 text-sm font-medium mb-5">
                  Sipariş ve bilgi talepleriniz için anında bize ulaşabilirsiniz.
                </p>
                <a
                  href="https://wa.me/905542345454"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-green-500 text-white text-sm font-bold hover:bg-green-600 transition-colors duration-200 shadow-lg shadow-green-500/20 active:scale-95"
                >
                  <Phone size={16} />
                  WhatsApp Yaz
                </a>
              </div>
            </motion.div>

            {/* Contact Form (Sağ Taraf) */}
            <motion.div
              initial={{ opacity: 0, x: 20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: 0.1 }}
              className="md:col-span-7"
            >
              {submitted ? (
                <div className="p-12 rounded-[3rem] bg-white border border-slate-100 text-center shadow-xl shadow-brand-dark/5">
                  <div className="w-20 h-20 mx-auto rounded-full bg-green-50 flex items-center justify-center mb-6">
                    <Send size={32} className="text-green-500" />
                  </div>
                  <h3 className="text-3xl font-black text-brand-dark tracking-tighter mb-4">
                    Mesajınız Alındı!
                  </h3>
                  <p className="text-brand-dark/50 font-medium text-lg mb-8">
                    Talebiniz uzman ekibimize ulaştı. En kısa sürede sizinle iletişime geçeceğiz.
                  </p>
                  <button
                    onClick={() => setSubmitted(false)}
                    className="text-sm font-bold text-brand-dark uppercase tracking-widest hover:text-brand-blue transition-colors"
                  >
                    Yeni Mesaj Gönder
                  </button>
                </div>
              ) : (
                <form
                  onSubmit={handleSubmit}
                  className="p-8 md:p-12 rounded-[3rem] bg-white border border-slate-100 shadow-xl shadow-brand-dark/5 space-y-8"
                >
                  <div className="grid md:grid-cols-2 gap-8">
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-dark/40 ml-2">
                        Ad Soyad
                      </label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({ ...form, name: e.target.value })}
                        required
                        className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand-dark/5 transition-all text-sm font-medium"
                        placeholder="Adınız Soyadınız"
                      />
                    </div>
                    <div className="space-y-2">
                      <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-dark/40 ml-2">
                        E-posta
                      </label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({ ...form, email: e.target.value })}
                        required
                        className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand-dark/5 transition-all text-sm font-medium"
                        placeholder="ornek@email.com"
                      />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-dark/40 ml-2">
                      Telefon
                    </label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      className="w-full h-14 px-6 rounded-2xl bg-slate-50 border-none focus:ring-2 focus:ring-brand-dark/5 transition-all text-sm font-medium"
                      placeholder="05XX XXX XX XX"
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[10px] font-bold uppercase tracking-[0.2em] text-brand-dark/40 ml-2">
                      Mesajınız
                    </label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({ ...form, message: e.target.value })}
                      required
                      rows={5}
                      className="w-full p-6 rounded-[2rem] bg-slate-50 border-none focus:ring-2 focus:ring-brand-dark/5 transition-all text-sm font-medium resize-none"
                      placeholder="Size nasıl yardımcı olabiliriz?"
                    />
                  </div>
                  {error && (
                    <p className="text-red-500 font-medium text-sm ml-2">{error}</p>
                  )}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="w-full py-5 rounded-full bg-brand-dark text-white text-lg font-black hover:bg-brand-pink transition-all duration-300 shadow-xl shadow-brand-dark/20 active:scale-95 flex items-center justify-center gap-3 disabled:opacity-50"
                  >
                    {submitting ? (
                      <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    ) : (
                      <>
                        <Send size={20} />
                        Mesajı Gönder
                      </>
                    )}
                  </button>
                </form>
              )}
            </motion.div>
          </div>
        </div>
      </section>
    </main>
  );
}